"use client";

import { useProfileMe } from "@myaccount/profile/_client/hooks";
import { useI18n } from "@wojo/localization";
import { StoryblokImage } from "@wojo/storyblok";
import { Markdown, Text } from "@wojo/ui";
import { getCurrency } from "@wojo/web-components";
import currency from "currency.js";
import { BaseLoyaltyOverviewCard } from "../base-loyalty-overview-card";
import styles from "./LoyaltyOverviewCard.module.scss";

export type LoyaltyOverviewCardProps = {
    showSeeDetailsLink?: boolean;
};

export const LoyaltyOverviewCard: React.FC<LoyaltyOverviewCardProps> = ({
    showSeeDetailsLink,
}) => {
    const { customer } = useProfileMe();
    const { formatCurrency, price } = getCurrency();
    const overviewI18n = useI18n("account").account.loyalty.loyaltyOverviewCard;

    if (!overviewI18n) {
        return null;
    }

    const repeatDiscount = price(
        customer?.loyaltyLevel?.repeatDiscount?.value ?? [],
    );
    const returnWindowDiscount = price(
        customer?.loyaltyLevel?.returnWindowDiscount?.value ?? [],
    );
    const totalDiscount = currency(repeatDiscount.value).add(
        returnWindowDiscount.value,
    );
    const repeatPromoCode = customer?.loyaltyLevel?.repeatPromoCode;

    return (
        <BaseLoyaltyOverviewCard
            badge={
                customer?.loyaltyLevel?.content?.icon ? (
                    <StoryblokImage
                        className={styles["badge"]}
                        mobile={{
                            asset: {
                                filename:
                                    customer.loyaltyLevel.content.icon.filename,
                                alt: customer.loyaltyLevel.content.icon.alt,
                            },
                            size: {
                                width: 160,
                                height: 160,
                            },
                        }}
                    />
                ) : undefined
            }
            content={
                <div className={styles.content}>
                    {customer?.loyaltyLevel?.content?.name && (
                        <div>
                            <Text.Body className={styles["column-title"]}>
                                {overviewI18n.levelTitle.l()}
                            </Text.Body>
                            <Text.Heading tag="div" order="4">
                                {customer.loyaltyLevel.content.name}
                            </Text.Heading>
                        </div>
                    )}
                    <div>
                        <Text.Body
                            className={styles["column-title"]}
                            emphasis={
                                !customer?.loyaltyLevel?.content ? "1" : "none"
                            }
                        >
                            {overviewI18n.discountsTitle.l()}
                        </Text.Body>
                        <Text.Heading tag="div" order="4">
                            {formatCurrency(totalDiscount.value)}
                        </Text.Heading>
                    </div>
                </div>
            }
            footer={
                overviewI18n.description && repeatPromoCode ? (
                    <Markdown
                        markdown={overviewI18n.description.l({
                            promoCode: repeatPromoCode,
                        })}
                        linksAreExternal
                    />
                ) : undefined
            }
            seeDetailsLink={
                showSeeDetailsLink && overviewI18n.seeDetailsLinkUrl.l()
                    ? {
                          accessibleText:
                              overviewI18n.seeDetailsLinkAccessibleText.l(),
                          url: overviewI18n.seeDetailsLinkUrl.l(),
                      }
                    : undefined
            }
        />
    );
};
